import Tooltip from "@/components/Tooltip";
import Icon from "@/components/Icon";
import { formatYearsAndMonths } from "./activeYearsBreakdown";

type SummaryRowProps = {
  label: string;
  value: string;
  tooltip?: string;
};

function SummaryRow({ label, value, tooltip }: SummaryRowProps) {
  return (
    <div className="flex justify-between gap-4 text-body-medium">
      <dt className="text-sys-brand-on-surface-variant flex items-center gap-1">
        {label}
        {tooltip && (
          <Tooltip
            variant="plain"
            supportingText={tooltip}
            color="black"
            positioning="fixed"
            anchorCorner="end-start"
            menuCorner="start-start"
          >
            <Icon name="info" size={16} className="text-sys-brand-on-surface-variant" />
          </Tooltip>
        )}
      </dt>
      <dd className="text-sys-brand-on-surface whitespace-nowrap m-0">{value}</dd>
    </div>
  );
}

const MIN_GAP_DAYS = 30;

const formatSignedDays = (days: number) => {
  const abs = Math.abs(days);
  return `${days < 0 ? "-" : "+"}${formatYearsAndMonths(abs)}`;
};

type Props = {
  totalDisplay: string;
  mergedBandDays: number;
  suspensionDays: number;
  systemActiveDays: number | null;
};

export default function ActiveYearsSummaryPanel({
  totalDisplay,
  mergedBandDays,
  suspensionDays,
  systemActiveDays
}: Props) {
  const adjustmentDays = systemActiveDays != null ? systemActiveDays - (mergedBandDays - suspensionDays) : 0;
  const showAdjustment = systemActiveDays != null && Math.abs(adjustmentDays) >= MIN_GAP_DAYS;

  return (
    <dl className="w-full flex flex-col gap-1 py-2 m-0" data-testid="active-years-breakdown-summary">
      <SummaryRow label="Member Band Time" value={formatYearsAndMonths(mergedBandDays)} />
      {suspensionDays > 0 && (
        <SummaryRow label="Time on Suspension" value={`-${formatYearsAndMonths(suspensionDays)}`} />
      )}
      {showAdjustment && (
        <SummaryRow
          label="Other CCMS Adjustments"
          value={formatSignedDays(adjustmentDays)}
          tooltip="Difference between band-level math shown here and the system's calculated active total. Reflects CCMS rules not exposed per band, such as classification ineligibility, cross-program time, or pre-program credit."
        />
      )}
      <div className="flex justify-between gap-4 pt-3 text-label-large font-bold">
        <dt className="text-sys-brand-on-surface m-0">Total Active Years of Membership</dt>
        <dd className="text-sys-brand-on-surface whitespace-nowrap m-0">{totalDisplay}</dd>
      </div>
    </dl>
  );
}
